// The UI shell controller (IMPLEMENTATION §3 M7; M6 decision 12 "UI intents for M7"): the glue between the
// game and the Preact chrome. It is the router's InputUi (mode, selection, hovered zone, drag, intents), sees
// every UI snapshot (10 Hz) and the SimEvents it needs (a GameController observer), and runs the timed parts
// once per display frame (`frame(now)`): the cold-open script (ui/intro.ts), Sarge's line, toasts, the ⟳
// handle, the goal chip's intro chips and the hint finger (tutorialView → ui/hints.ts). Components read the
// signals of ui/state.ts and act through `ShellActions` (ui/ctx.ts, built by main.ts). No sim rules here:
// commands go through the player's dispatch, text through copy.json, numbers through ui.json.
import type { CopyData, UiData } from '../data/schema.ts';
import type { HintFinger } from '../fx/HintFinger.ts';
import type { SimObserver } from '../game/observer.ts';
import type { UiSnapshot } from '../game/snapshot.ts';
import type { Mode } from '../input/modes.ts';
import { type Names, namesOf } from '../input/preview.ts';
import type { InputUi, UiIntent } from '../input/router.ts';
import type { Rect } from '../layout/computeLayout.ts';
import type { SimDefs } from '../sim/defs.ts';
import type { SimEvent } from '../sim/events.ts';
import type { BType, TipId } from '../sim/gen/ids.ts';
import { type GoalGhost, hasFeature, isMachine, tutorialView } from '../sim/query.ts';
import type { SimState } from '../sim/state.ts';
import { type CardTarget, cardKindOf } from './cards/model.ts';
import { copyRaw, copyText, fillCopy } from './copy.ts';
import { type Fmt, ghostItems, money } from './format.ts';
import {
  ghostGesture,
  type HintCtx,
  type HintGesture,
  type HintRectId,
  hintGesture,
} from './hints.ts';
import { howtoFlag, howtoSteps, howtoTypesOf } from './howto.ts';
import {
  chipShown,
  type IntroState,
  introLineKey,
  replayIntro,
  startIntro,
  stepIntro,
} from './intro.ts';
import { hintsOn } from './settings.ts';
import {
  bannerSig,
  chipIntroSig,
  draggingSig,
  freshSig,
  hoverZoneSig,
  howtoSig,
  introSig,
  layoutSig,
  modeSig,
  NO_PANEL,
  type Panel,
  panelSig,
  popoverSig,
  rotateHandleSig,
  rotateSig,
  sargeSig,
  selectionSig,
  sheetSig,
  statusSig,
  toastsSig,
} from './state.ts';
import {
  expireToasts,
  NO_TOASTS,
  pauseToasts,
  pushToast,
  removeToast,
  resumeToasts,
  type ToastInput,
} from './toastQueue.ts';

/** One-time UI flags kept in the save's meta.uiFlags (the A2HS card keeps its own M11 flag `a2hs`). */
export type UiFlag = 'ruleBanner' | 'awayToast' | `howto_${string}`;

export interface ShellDeps {
  copy(): CopyData;
  ui(): UiData;
  defs(): SimDefs;
  state(): SimState;
  mode(): Mode;
  now(): number;
  finger: HintFinger | null;
  /** Client rect of a DOM element the finger may point at. */
  rect(id: HintRectId): Rect | null;
  flags: { has(f: UiFlag): boolean; set(f: UiFlag): void };
  /** The building how-to cards show on unlock (env.howto; off in ?test runs unless ?howto=1). */
  howto?: boolean;
}

export interface ShellDiag {
  panel: Panel['k'];
  card: CardTarget | null;
  sheet: string;
  toasts: { kind: string; text: string; token: number | null }[];
  sarge: string | null;
  intro: IntroState['phase'];
  hint: { tip: string | null; gesture: HintGesture['k'] | null };
  banner: string | null;
  howto: string | null;
  fresh: string[];
  rotate: boolean;
  chipIntro: number;
}

export class Shell {
  private readonly d: ShellDeps;
  private names: Names;
  private namesFor: SimDefs;
  /** Transient Sarge line (a hint / status / nudge intent) and when it ends. */
  private line: { text: string; key: string; until: number } | null = null;
  private seenUnlocked: string[] = [];
  private seenShop: string[] = [];
  private builtMachine = false;
  /** Building types whose how-to card waits to show (one at a time, after the banners). */
  private howtoQueue: BType[] = [];
  private goalIndex = -2;
  private goalSince = 0;
  private bannerUntil = 0;
  private fingerKey = '';
  private lastInputAt = 0;
  private fingerShownAt = 0;
  /** The active tutorial tip (tutorialView, read once per snapshot and after a rebase). */
  private tip: { id: TipId; copy: string } | null = null;
  private gesture: HintGesture | null = null;
  /** The goal's item placed as a ghost (snapshot goal.ghost): Sarge and the finger ask for its materials. */
  private ghost: GoalGhost | null = null;
  private ghostName = '';
  /** The player's own tap emptied the practice tin (`empty`): the cold open's two lines play. */
  private emptied = false;

  constructor(deps: ShellDeps) {
    this.d = deps;
    this.namesFor = deps.defs();
    this.names = namesOf(this.namesFor);
    const now = deps.now();
    this.lastInputAt = now;
    this.rebase(now);
  }

  /** Formatting context (names follow a DEV data reload). */
  fmt(): Fmt {
    const defs = this.d.defs();
    if (defs !== this.namesFor) {
      this.namesFor = defs;
      this.names = namesOf(defs);
    }
    return {
      copy: this.d.copy(),
      numberFormat: this.d.ui().numberFormat,
      names: this.names,
      defs,
      eta: this.d.ui().goalChip,
    };
  }

  /** A new game (boot, save load, reset): forget what was seen, restart the intro from the sim. */
  rebase(now: number): void {
    const s = this.d.state();
    this.seenUnlocked = s.progress.unlocked.slice();
    this.seenShop = s.progress.seen.slice();
    this.builtMachine = s.progress.firstBuilt.some((t) => isMachine(t));
    introSig.value = startIntro(this.d.mode() === 'coldOpen', now);
    this.emptied = false;
    this.goalIndex = -2;
    this.line = null;
    this.tip = tutorialView(s, this.d.defs()).tip;
    freshSig.value = [];
    toastsSig.value = NO_TOASTS;
    bannerSig.value = null;
    howtoSig.value = null;
    this.howtoQueue = [];
    rotateHandleSig.value = null;
    popoverSig.value = null;
    statusSig.value = null;
    chipIntroSig.value = 0;
    this.closePanel();
    this.fingerKey = '';
    this.d.finger?.hide();
  }

  // ---- InputUi (the router's sink) ----------------------------------------------------------------------

  readonly inputUi: InputUi = {
    sheet: (st) => {
      if (st === 'peek' && sheetSig.value === 'open') sheetSig.value = 'peek';
      else if (st === 'restore' && sheetSig.value === 'peek')
        sheetSig.value = panelSig.value.k === 'shop' ? 'open' : 'closed';
    },
    hoverZone: (z) => {
      hoverZoneSig.value = z;
    },
    mode: (v) => {
      const prev = modeSig.value;
      modeSig.value = v;
      // a mode switch closes the cards (Info in Play ≠ Machine card in Build) and the pop-over
      if (prev && prev.mode !== v.mode) {
        if (panelSig.value.k === 'card') this.closePanel();
        popoverSig.value = null;
      }
    },
    selection: (v) => {
      selectionSig.value = v;
      if (v === null && statusSig.value !== null && this.line?.key === 'status') {
        statusSig.value = null;
        this.line = null;
      }
    },
    dragging: (on) => {
      draggingSig.value = on;
      const now = this.d.now();
      toastsSig.value = on ? pauseToasts(toastsSig.value, now) : resumeToasts(toastsSig.value, now);
      if (on && (panelSig.value.k === 'card' || panelSig.value.k === 'goal')) this.closePanel();
      if (on) popoverSig.value = null;
    },
    intent: (i) => this.intent(i),
  };

  private intent(i: UiIntent): void {
    const copy = this.d.copy();
    const now = this.d.now();
    switch (i.k) {
      case 'shop': {
        const open = i.open === 'toggle' ? panelSig.value.k !== 'shop' : i.open;
        if (open) this.openShop();
        else this.closePanel();
        return;
      }
      case 'info':
        if (i.target.k === 'item') this.openCard({ k: 'item', id: i.target.id });
        else if (i.target.k === 'building') this.openCard({ k: 'building', id: i.target.id });
        return;
      case 'card':
        // the "Out of ammo!" beat: the player's rapid extra taps on the emptied Gallery do not open its card
        // over the script (the input is already in PLAY: the sim ended the practice at `empty`, M7 #2)
        if (introSig.value.phase === 'outOfAmmo') return;
        if (i.target.k === 'building') this.openCard({ k: 'building', id: i.target.id });
        else if (i.target.k === 'rock') this.openCard({ k: 'rock', x: i.target.x, y: i.target.y });
        return;
      case 'undoToast':
        this.toast({
          kind: 'undo',
          text: fillCopy(copy.toast.sold ?? '', { value: money(this.fmt(), i.value) }),
          ms: this.d.ui().toasts.undoMs,
          token: i.token,
        });
        return;
      case 'rotateHandle':
        rotateHandleSig.value = { bid: i.bid, until: now + i.ms };
        return;
      case 'hint':
        this.say(i.text, 'hint');
        return;
      case 'status':
        statusSig.value = i.text;
        this.say(i.text, 'status');
        return;
      case 'nudge':
        this.say(copy.sarge.tapToFire ?? '', 'nudge');
        this.fingerKey = ''; // re-pulse the finger on the Gallery
        return;
    }
  }

  // ---- panels ---------------------------------------------------------------------------------------------

  openShop(): void {
    popoverSig.value = null;
    panelSig.value = { k: 'shop' };
    sheetSig.value = 'open';
  }

  openCard(target: CardTarget): void {
    popoverSig.value = null;
    panelSig.value = { k: 'card', target, more: false };
    sheetSig.value = 'closed';
  }

  openPanel(p: Panel): void {
    if (p.k === 'shop') {
      this.openShop();
      return;
    }
    popoverSig.value = null;
    panelSig.value = p;
    sheetSig.value = 'closed';
  }

  closePanel(): void {
    const was = panelSig.value.k;
    panelSig.value = NO_PANEL;
    sheetSig.value = 'closed';
    popoverSig.value = null;
    if (was === 'shop') freshSig.value = []; // the NEW badges were seen
  }

  togglePanel(k: 'shop' | 'settings' | 'goal'): void {
    if (panelSig.value.k === k) this.closePanel();
    else if (k === 'shop') this.openShop();
    else this.openPanel(k === 'settings' ? { k: 'settings' } : { k: 'goal' });
  }

  /** The card's "More" / "Less" toggle. */
  toggleMore(): void {
    const p = panelSig.value;
    if (p.k === 'card') panelSig.value = { ...p, more: !p.more };
  }

  /** A pointerdown on the board (#input): the finger's idle clock; a board touch closes a card / pop-over. */
  noteInput(onBoard: boolean): void {
    this.lastInputAt = this.d.now();
    if (!onBoard) return;
    const k = panelSig.value.k;
    if (k === 'card' || k === 'goal') this.closePanel();
    popoverSig.value = null;
  }

  // ---- toasts and Sarge -------------------------------------------------------------------------------------

  toast(t: ToastInput): void {
    let st = toastsSig.value;
    // the first merge's tutorial toast collapses under an Undo toast: the two stacked over the board's
    // bottom row hid the Gallery right after the first sale (v0.1 playtest)
    const tut = t.kind === 'undo' ? st.list.find((x) => x.key === 'twoMakeOne') : undefined;
    if (tut) st = removeToast(st, tut.id);
    toastsSig.value = pushToast(st, t, this.d.now(), this.d.ui().toasts.max);
  }

  dismissToast(id: number): void {
    toastsSig.value = removeToast(toastsSig.value, id);
  }

  /** A transient Sarge line (ui.json sarge.lineMs). */
  say(text: string, key: string): void {
    if (text === '') return;
    this.line = { text, key, until: this.d.now() + this.d.ui().sarge.lineMs };
  }

  /** Back from the background or a returning session: the one-time "Workshop pauses" toast (v0.1). */
  onReturn(): void {
    if (this.d.flags.has('awayToast')) return;
    this.d.flags.set('awayToast');
    this.toast({
      kind: 'info',
      text: this.d.copy().toast.awayPause ?? '',
      ms: this.d.ui().toasts.awayMs,
      key: 'awayPause',
    });
  }

  replayIntro(): void {
    this.closePanel();
    introSig.value = replayIntro(introSig.value, this.d.now());
  }

  /** The completion banner's "Keep playing". */
  dismissBanner(): void {
    bannerSig.value = null;
  }

  /** Whether `type` has a how-to card (the "?" button on its card). */
  hasHowto(type: BType): boolean {
    return howtoSteps(this.d.copy(), this.d.defs(), type, true).length > 0;
  }

  /** Opens the how-to card of `type` now (the "?" button); the card closes. */
  showHowto(type: BType): void {
    if (!this.hasHowto(type)) return;
    this.closePanel();
    this.d.flags.set(howtoFlag(type));
    this.howtoQueue = this.howtoQueue.filter((t) => t !== type);
    howtoSig.value = type;
  }

  /** The how-to card's "Got it!". */
  dismissHowto(): void {
    howtoSig.value = null;
  }

  // ---- snapshots and events -------------------------------------------------------------------------------

  /** Every UI snapshot (10 Hz): unlock toasts / banners, Shop reveals, the rule banner, the goal's intro chips. */
  onSnapshot(snap: UiSnapshot, now: number): void {
    const s = this.d.state();
    const defs = this.d.defs();
    const copy = this.d.copy();
    const ui = this.d.ui();
    this.tip = tutorialView(s, defs).tip;
    this.ghost = snap.goal?.ghost ?? null;
    const target = snap.goal?.target;
    this.ghostName = target?.type === 'shop' ? this.fmt().names.shop(target.id) : '';
    // unlocks with a toast / banner (u07, u19; u21 complete); u03 = the first merge's one-time toast
    if (s.progress.unlocked.length !== this.seenUnlocked.length) {
      for (const id of s.progress.unlocked) {
        if (this.seenUnlocked.includes(id)) continue;
        const u = defs.unlocks.find((x) => x.id === id);
        if (u?.toast)
          this.toast({
            kind: 'unlock',
            text: copyText(copy, u.toast),
            ms: ui.toasts.infoMs,
            key: id,
          });
        if (u?.banner) bannerSig.value = 'complete';
        if (u && this.d.howto === true && hintsOn.value)
          for (const t of howtoTypesOf(
            u.effects.map((e) => e.id).filter((e) => hasFeature(s, defs, e)),
          ))
            if (!this.d.flags.has(howtoFlag(t)) && !this.howtoQueue.includes(t) && this.hasHowto(t))
              this.howtoQueue.push(t);
        if (id === 'u03')
          this.toast({
            kind: 'info',
            text: copy.toast.twoMakeOne ?? '',
            ms: ui.toasts.infoMs,
            key: 'twoMakeOne',
          });
      }
      this.seenUnlocked = s.progress.unlocked.slice();
    }
    if (s.progress.seen.length !== this.seenShop.length) {
      const fresh = s.progress.seen.filter((x) => !this.seenShop.includes(x));
      this.seenShop = s.progress.seen.slice();
      if (fresh.length > 0 && panelSig.value.k !== 'shop')
        freshSig.value = [...freshSig.value, ...fresh];
    }
    if (!this.builtMachine && s.progress.firstBuilt.some((t) => isMachine(t))) {
      this.builtMachine = true;
      if (!this.d.flags.has('ruleBanner')) {
        this.d.flags.set('ruleBanner');
        if (bannerSig.value === null) {
          bannerSig.value = 'rule';
          this.bannerUntil = now + ui.banner.ruleMs;
        }
      }
    }
    // a card whose entity is gone without a board touch (merged by Merge All, pulled by a machine, sold):
    // close it, so the hint finger, the toast positions and the diagnostics see no panel
    const p = panelSig.value;
    if (p.k === 'card' && cardKindOf(s, p.target, this.d.mode()) === null) this.closePanel();
    const gi = snap.goal?.index ?? -1;
    if (gi !== this.goalIndex) {
      this.goalIndex = gi;
      this.goalSince = now;
      chipIntroSig.value = 0;
    }
  }

  /** Every SimEvent (GameController observer; silent advances included — they only add toasts). */
  readonly observer: SimObserver = {
    onSim: () => undefined,
    onEvents: (evs: readonly SimEvent[]) => {
      for (const e of evs) {
        if (e.e === 'empty') this.emptied = true;
        else if (e.e === 'bmerge' && e.other !== 0) {
          this.toast({
            kind: 'info',
            text: this.d.copy().toast.machineMergeNoUndo ?? '',
            ms: this.d.ui().toasts.infoMs,
            key: 'machineMerge',
          });
        } else if (e.e === 'sell' && e.where === 'ghost') {
          this.toast({
            kind: 'info',
            text: fillCopy(this.d.copy().toast.ghostCredited ?? '', {
              value: money(this.fmt(), e.value),
            }),
            ms: this.d.ui().toasts.infoMs,
          });
        }
      }
    },
  };

  // ---- per frame ------------------------------------------------------------------------------------------

  frame(now: number): void {
    const ui = this.d.ui();
    // cold-open script
    const intro = stepIntro(
      introSig.value,
      this.d.mode() === 'coldOpen',
      now,
      ui.coldOpen,
      this.emptied,
    );
    if (intro !== introSig.value) introSig.value = intro;
    // toasts, the ⟳ handle, the rule banner, Sarge's transient line
    const t = expireToasts(toastsSig.value, now);
    if (t !== toastsSig.value) toastsSig.value = t;
    const h = rotateHandleSig.value;
    if (h !== null && now >= h.until) rotateHandleSig.value = null;
    if (bannerSig.value === 'rule' && now >= this.bannerUntil) bannerSig.value = null;
    // the next building how-to card, once no banner or other how-to card is up
    if (howtoSig.value === null && bannerSig.value === null && intro.phase === 'play') {
      const next = this.howtoQueue.shift();
      if (next !== undefined) {
        this.d.flags.set(howtoFlag(next));
        howtoSig.value = next;
      }
    }
    if (this.line !== null && now >= this.line.until) {
      if (this.line.key === 'status') statusSig.value = null;
      this.line = null;
    }
    // the goal's earlier chips (ui.json goalChip.introMs each; only once the chip shows)
    if (!chipShown(intro.phase)) this.goalSince = now;
    const step = Math.floor((now - this.goalSince) / Math.max(1, ui.goalChip.introMs));
    if (step !== chipIntroSig.value && step <= ui.goalChip.introMax) chipIntroSig.value = step;
    this.updateSarge(intro);
    this.updateFinger(intro, now);
  }

  private updateSarge(intro: IntroState): void {
    const copy = this.d.copy();
    const lineKey = introLineKey(intro.phase);
    let next: { text: string; key: string } | null = null;
    if (lineKey !== null) next = { text: copyRaw(copy, lineKey) ?? '', key: lineKey };
    else if (this.line !== null) next = { text: this.line.text, key: this.line.key };
    else if (this.ghost !== null && intro.phase === 'play' && hintsOn.value)
      next = {
        text: fillCopy(copy.hint.feedGhost ?? '{items}', {
          items: ghostItems(this.fmt(), this.ghost),
          name: this.ghostName,
        }),
        key: 'ghost',
      };
    else if (this.tip !== null && (intro.phase === 'cold' || hintsOn.value))
      next = { text: copyText(copy, this.tip.copy), key: `tip:${this.tip.id}` };
    const cur = sargeSig.value;
    if (
      next === null ? cur !== null : cur === null || cur.text !== next.text || cur.key !== next.key
    )
      sargeSig.value = next;
  }

  private updateFinger(intro: IntroState, now: number): void {
    const finger = this.d.finger;
    if (!finger) return;
    const layout = layoutSig.value;
    const tip = intro.phase === 'cold' || intro.phase === 'play' ? this.tip : null;
    const ghost = intro.phase === 'play' ? this.ghost : null;
    const p = panelSig.value.k;
    const allowed =
      layout !== null &&
      (tip !== null || ghost !== null) &&
      (intro.phase === 'cold' || hintsOn.value) &&
      !draggingSig.value &&
      rotateSig.value === null &&
      (p === 'none' || (p === 'shop' && ghost === null && tip?.id === 'dropOnVein'));
    let g: HintGesture | null = null;
    let down = false;
    if (allowed && layout !== null) {
      const mv = modeSig.value;
      const sel = selectionSig.value;
      const ctx: HintCtx = {
        s: this.d.state(),
        d: this.d.defs(),
        layout,
        mode: this.d.mode(),
        tool: mv?.tool ?? 'move',
        shopOpen: p === 'shop' && sheetSig.value === 'open',
        popover: popoverSig.value !== null,
        placing: sel !== null && sel.k === 'shop' ? sel.item : null,
        rect: (id) => this.d.rect(id),
      };
      if (ghost !== null) g = ghostGesture(ghost, ctx);
      else if (tip !== null) g = hintGesture(tip.id, ctx);
      // a tap finger hangs below its tip: when that would cover Sarge's line, point down from above
      const b = g !== null && g.k === 'tap' ? this.d.rect('sarge') : null;
      if (b !== null && g !== null && g.k === 'tap') {
        const size = layout.cellCss;
        const x0 = g.at.x - size / 2;
        down = x0 < b.x + b.w && x0 + size > b.x && g.at.y < b.y + b.h && g.at.y + size > b.y;
      }
    }
    this.gesture = g;
    const r = (n: number): number => Math.round(n);
    const key =
      g === null
        ? ''
        : g.k === 'tap'
          ? `tap:${r(g.at.x)},${r(g.at.y)}${down ? 'v' : ''}`
          : `drag:${r(g.from.x)},${r(g.from.y)}>${r(g.to.x)},${r(g.to.y)}`;
    const idle = now - Math.max(this.lastInputAt, this.fingerShownAt);
    const repulse =
      key !== '' && idle >= this.d.ui().hints.fingerRepulseMs && !finger.state().looping;
    if (key === this.fingerKey && !repulse) return;
    this.fingerKey = key;
    this.fingerShownAt = now;
    if (g === null) finger.hide();
    else if (g.k === 'tap') finger.tap(g.at.x, g.at.y, down);
    else finger.drag(g.from, g.to);
  }

  // ---- diagnostics ------------------------------------------------------------------------------------------

  diag(): ShellDiag {
    const p = panelSig.value;
    return {
      panel: p.k,
      card: p.k === 'card' ? p.target : null,
      sheet: sheetSig.value,
      toasts: toastsSig.value.list.map((t) => ({
        kind: t.kind,
        text: t.text,
        token: t.token ?? null,
      })),
      sarge: sargeSig.value?.text ?? null,
      intro: introSig.value.phase,
      hint: { tip: this.tip?.id ?? null, gesture: this.gesture?.k ?? null },
      banner: bannerSig.value,
      howto: howtoSig.value,
      fresh: freshSig.value.slice(),
      rotate: rotateSig.value !== null,
      chipIntro: chipIntroSig.value,
    };
  }

  /** The names helper (cards, shop). */
  namesOf(): Names {
    this.fmt();
    return this.names;
  }
}
